import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from '../lib/cn';
import { SpinnerIcon } from './icons';
import { KeyChord, type IKey } from './keycap';

export type TButtonVariant = 'primary' | 'secondary' | 'ghost';
export type TButtonSize = 'md' | 'sm' | 'xs';

interface IButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: TButtonVariant;
  size?: TButtonSize;
  block?: boolean;
  waiting?: boolean;
  waitingLabel?: string;
  hint?: readonly IKey[];
  destructive?: boolean;
  children: ReactNode;
}

const SIZE: Record<TButtonSize, string> = {
  md: 'h-control text-body',
  sm: 'h-control-sm text-data',
  xs: 'h-control-xs min-w-control-xs text-meta rounded-item',
};

// `secondary` pads like `primary`: side by side in a footer, less padding reads as shorter.
const PAD: Record<TButtonVariant, Record<TButtonSize, string>> = {
  primary: { md: 'px-6', sm: 'px-input', xs: 'px-canvas-x' },
  secondary: { md: 'px-6', sm: 'px-input', xs: 'px-canvas-x' },
  ghost: { md: 'px-flat', sm: 'px-flat', xs: 'px-canvas-x' },
};

const VARIANT: Record<TButtonVariant, string> = {
  primary: 'bg-accent text-surface font-semibold disabled:bg-accent-waiting',
  secondary: 'bg-ghost text-primary font-medium enabled:hover:bg-hover-raised disabled:text-faint',
  ghost:
    'text-muted font-medium enabled:hover:bg-ghost enabled:hover:text-primary disabled:text-faint',
};

const DESTRUCTIVE =
  'border border-danger-border text-danger-text bg-transparent disabled:border-border-raised disabled:text-faint';

export const Button = ({
  variant = 'primary',
  size = 'md',
  block,
  waiting,
  waitingLabel,
  hint,
  destructive,
  className,
  disabled,
  children,
  type = 'button',
  ...rest
}: IButtonProps) => (
  <button
    type={type}
    disabled={disabled || waiting}
    className={cn(
      'inline-flex items-center justify-center gap-2 rounded-control whitespace-nowrap',
      SIZE[size],
      PAD[variant][size],
      VARIANT[variant],
      destructive && DESTRUCTIVE,
      block && 'w-full',
      block && variant === 'ghost' && 'bg-ghost',
      className,
    )}
    {...rest}
  >
    {waiting && <SpinnerIcon />}
    {waiting ? (waitingLabel ?? children) : children}
    {/* Shown while disabled too: a chord visible only once the form is valid is a chord
        nobody learns. */}
    {hint && (
      <KeyChord
        keys={hint}
        tone={disabled || waiting ? 'muted' : variant === 'primary' ? 'on-accent' : 'on-plate'}
      />
    )}
  </button>
);
